import { LinkedInV2Logo } from '@awpmarine/ui/components/logos';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@awpmarine/ui/components/ui/breadcrumb';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';

import { ContentLanguageNotice, contentLang } from '@/components/content-language';
import { MarkdownDocs } from '@/components/markdown';
import { findRole, formatPay, formatRoleType, isOpen } from '@/lib/careers';
import { formatDate } from '@/lib/dates';
import { pageHead } from '@/lib/head';
import { ogImage } from '@/lib/open-graph';
import { careers, isTranslated, translatedLocales } from '@/lib/source';
import { m } from '@/paraglide/messages';

import { ApplicationForm } from '../-application-form';

export const Route = createFileRoute('/careers/$slug/')({
  // No server function: this runs in the browser on client-side navigation too (ADR 015).
  // A closed or filled role is a 404. Decided here, not in render, so the page hydrates with the build's answer.
  loader: async ({ params }) => {
    const role = findRole(params.slug);
    if (!role || !isOpen(role)) throw notFound();
    return {
      title: role.data.title,
      description: role.data.description ?? '',
      url: role.url,
      translated: translatedLocales(careers, role.slugs),
      // Only the built page needs it, which keeps react-dom/server out of the browser bundle.
      jobPostingLd: import.meta.env.SSR ? (await import('@/lib/job-posting')).jobPostingLd(role) : undefined,
    };
  },
  head: ({ loaderData }) =>
    pageHead({
      title: loaderData?.title ?? m.careers_title(),
      description: loaderData?.description ?? '',
      canonical: loaderData?.url ?? '/careers',
      image: loaderData ? ogImage(loaderData.url) : undefined,
      translated: loaderData?.translated,
      breadcrumbs: loaderData && [
        { name: m.role_breadcrumb(), path: '/careers' },
        { name: loaderData.title, path: loaderData.url },
      ],
      scripts: loaderData?.jobPostingLd ? [{ type: 'application/ld+json', children: loaderData.jobPostingLd }] : [],
    }),
  component: RolePage,
});

function RolePage() {
  const { slug } = Route.useParams();
  const role = findRole(slug);
  if (!role) throw notFound();

  const { title, description, type, location, pay, posted, closes, linkedin, body } = role.data;
  const translated = isTranslated(role);
  const details = [
    { label: m.role_detail_type(), value: formatRoleType(type) },
    { label: m.role_detail_location(), value: location },
    ...(pay ? [{ label: m.role_detail_pay(), value: formatPay(pay) }] : []),
    { label: m.role_detail_posted(), value: formatDate(posted) },
    { label: m.role_detail_closes(), value: formatDate(closes) },
  ];

  return (
    <>
      <section className='bg-mist'>
        <div className='mx-auto w-full max-w-310 px-5 py-12 sm:px-8 sm:pt-14'>
          <Breadcrumb className='mb-5'>
            <BreadcrumbList className='gap-2 text-base sm:gap-2'>
              <BreadcrumbItem>
                <BreadcrumbLink render={<Link to='/careers' />} className='hover:text-brand'>
                  {m.role_breadcrumb()}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage className='text-subtle'>{title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <h1
            lang={contentLang(translated)}
            className='text-[clamp(34px,4vw,50px)] leading-[1.1] font-bold tracking-[-0.02em]'
          >
            {title}
          </h1>
          <ContentLanguageNotice translated={translated} />
        </div>
      </section>

      <section className='mx-auto flex w-full max-w-310 flex-wrap items-start gap-14 px-5 py-14 sm:px-8 sm:py-16'>
        <article lang={contentLang(translated)} className='max-w-190 min-w-0 flex-[999_1_560px]'>
          {description && <p className='text-ink mb-6 text-xl/relaxed'>{description}</p>}
          <MarkdownDocs body={body} />
        </article>
        <aside className='bg-mist flex min-w-0 flex-[1_1_320px] flex-col gap-6 rounded-3xl p-8'>
          <div className='flex flex-col gap-2.5'>
            <a
              href='#apply'
              className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-brand px-7 font-semibold text-white transition-colors hover:bg-[#1f4f8a] disabled:opacity-70'
            >
              {m.role_apply()}
            </a>
            {linkedin && (
              <a
                href={linkedin}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full border-[1.5px] border-[#0a66c2] bg-white px-6 font-semibold text-[#0a66c2]'
              >
                <LinkedInV2Logo className='size-4.5' />
                {m.role_apply_linkedin()}
              </a>
            )}
          </div>
          <dl className='flex flex-col gap-4'>
            {details.map((detail) => (
              <div key={detail.label}>
                <dt className='text-subtle text-sm'>{detail.label}</dt>
                <dd className='font-semibold'>{detail.value}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </section>

      <section id='apply' className='bg-mist scroll-mt-6'>
        <div className='mx-auto flex w-full max-w-310 flex-wrap items-start gap-12 px-5 py-16 sm:px-8 sm:py-22'>
          <div className='min-w-0 flex-[1_1_340px]'>
            <h2 className='mb-4 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
              {m.role_application_title()}
            </h2>
            <p className='text-body text-[17px]'>
              <ParaglideMessage
                message={m.role_applying_for}
                inputs={{ title }}
                markup={{ role: ({ children }) => <strong className='text-ink'>{children}</strong> }}
              />
            </p>
          </div>
          <ApplicationForm role={{ slug, title }} className='min-w-0 flex-[1.4_1_520px] bg-white' />
        </div>
      </section>
    </>
  );
}
